import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import _ from "lodash";
import { Observable, combineLatest, merge } from "rxjs";
import {
    filter,
    map,
    scan,
    take,
    tap,
    withLatestFrom
} from "rxjs/operators";
import { InputProcessorType } from "~app/common/components/input/input-processor";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, VirkeskopAffarsformer, VirkeskopNettotyper } from "~app/common/models/constants";
import { RuleErrorContext, RuleSeverityLevel, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VirkeskopskontraktPrisDTO } from "~app/features/virkeskop/dto/pristabell/pristabell-dtos";
import { Kontrakt } from "~app/features/virkeskop/models/kontrakt-models";
import { PristabellRad } from "~app/features/virkeskop/models/pristabell/pristabell-models";
import { ErsattningsTyp, KontraktUtskriftValues } from "~app/features/virkeskop/models/utskrift/utskriftValues-models";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { LeverantorMediatorService } from "~app/features/virkeskop/services/leverantor-mediator.service";
import { UtskriftMediatorService } from "~app/features/virkeskop/services/utskrift-mediator.service";
import { VirkeskopValidationService } from "~app/features/virkeskop/services/validation/virkeskop-validation.service";
import { VirkeskopskontraktLeverantorMediatorService } from "~app/features/virkeskop/services/virkeskopskontrakt-leverantor-mediator.service";
import { hasKontraktGruppering } from "~app/features/virkeskop/utils/utskrift/utskrift-utilities";
import { ILeverantor } from "~app/features/virkeskop/virkesleverantor/models/virkesleverantorModel";
import { canEdit } from "~app/role-permissions";
import { DisabledControlDirective } from "../../../../../common/directives/disabled-control/disabled-control.directive";
import { KontraktGrupperingComponent } from "../kontrakt-gruppering/kontrakt-gruppering.component";
import { KontraktPristabellComponent } from "../kontrakt-pristabell/kontrakt-pristabell.component";
import { KontraktVolymBeloppComponent } from "../kontrakt-volym-belopp/kontrakt-volym-belopp.component";
import { KontraktUtskriftForm, PrisradForm } from "../kontraktUtskriftForm";
type LeverantorItem = Pick<ILeverantor, "guid" | "namn">;

@Component({
    selector: "rask-kontrakt-justera-utskrift",
    templateUrl: "./kontrakt-justera-utskrift.component.html",
    styleUrls: ["./kontrakt-justera-utskrift.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        DisabledControlDirective,
        KontraktGrupperingComponent,
        HeaderComponent,
        KontraktVolymBeloppComponent,
        KontraktPristabellComponent,
        CheckboxComponent,
        DatepickerComponent,
        TextareaComponent,
        MultiSelectorComponent,
        AsyncPipe
    ]
})
export class KontraktJusteraUtskriftComponent {
    protected form = new FormGroup<KontraktUtskriftForm>({
        stamplingsLangdUpprattatDatum: new FormControl<string | null>(null),
        stamplingsLangdUpprattatAv: new FormControl("", { nonNullable: true }),
        stamplingsLangdAntalTrad: new FormControl<number | null>(null),
        kontraktsbelopp: new FormControl<number | null>(null),
        visaKontraktsbeloppIUtskrift: new FormControl(false, { nonNullable: true }),
        kontraktsbeloppUtskrivet: new FormControl("", { nonNullable: true }),
        ovrigtOverenskommet: new FormControl("", { nonNullable: true }),
        makeGodkannandeKravsEj: new FormControl<boolean | null>(null),
        leveransvirkesplanIUtskrift: new FormControl<boolean | null>(null),
        drivningskostnad: new FormControl<number | null>(null),
        arealSlutavvUtanFrotrad: new FormControl<number | null>(null),
        arealSlutavvMedFrotrad: new FormControl<number | null>(null),
        arealOverstandare: new FormControl<number | null>(null),
        arealGallring: new FormControl<number | null>(null),
        arealOvrigAvverkning: new FormControl<number | null>(null),
        kontraktTotalKvantitet: new FormControl<number | null>(null),
        ersattningstyp: new FormControl<ErsattningsTyp | null>(null),
        pristabell: new FormArray<FormGroup<PrisradForm>>([])
    });

    protected readonly isReadonly$: Observable<boolean>;
    protected kontrakt?: Kontrakt;
    protected leverantorItems$: Observable<LeverantorItem[]>;
    protected selectedLeverantorer = new FormControl<string[]>([]);
    protected utskrift = signal<KontraktUtskriftValues>(null);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly leverantorMediatorService = inject(LeverantorMediatorService);
    private readonly modalService = inject(RaskModalService);
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly virkeskopskontraktLeverantorMediatorService = inject(VirkeskopskontraktLeverantorMediatorService);
    private readonly virkeskopValidationService = inject(VirkeskopValidationService);

    protected get aterstallDisabled() {
        const skickadTillVacs = this.kontrakt?.skickadTillVacs;
        return !_.isNil(skickadTillVacs);
    }

    protected get seIntProcessor() {
        return InputProcessorType.seLocaleIntProcessor;
    }

    protected get showKontraktGruppering() {
        return hasKontraktGruppering(this.kontrakt);
    }

    protected get showMakeGodkannandeKravsEj() {
        return this.kontrakt.isRotpost
            || this.kontrakt.isAffarsformU4
            || this.kontrakt.isAffarsformU5
            || this.kontrakt.isAffarsformU6;
    }

    protected get showPristabell() {
        return this.kontrakt.isLeveransvirke
            || this.kontrakt.isAffarsformU4
            || this.kontrakt.isAffarsformU5
            || this.kontrakt.isAffarsformU6;
    }

    protected get showVolymOchBelopp() {
        return this.kontrakt.isAffarsformU4
            || this.kontrakt.isAffarsformU5
            || this.kontrakt.isAffarsformU6;
    }

    private readonly canEdit = canEdit();

    constructor() {
        this.isReadonly$ = this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            tap(readonly => {
                if (readonly) {
                    this.form.disable({ emitEvent: false });
                }
            })
        );

        this.form.valueChanges
            .pipe(
                takeUntilDestroyed()
            ).subscribe(() => {
                const values = this.form.getRawValue();
                const rows = this.utskrift().pristabell;

                const enforceNumber = (val: number): number => {
                    if (typeof val === "number") {
                        return val;
                    }
                    return 0;
                };

                const pristabell = values.pristabell.map((formRad, index): VirkeskopskontraktPrisDTO => {
                    const row = rows[index];
                    const {
                        sortimentId, tillaggAvdrag, sortimentsnetto, fsc, pefc, ...rest
                    } = formRad;
                    return {
                        ...row.toDto(),
                        ...rest,
                        tillaggAvdrag: enforceNumber(tillaggAvdrag),
                        sortimentsnetto: enforceNumber(sortimentsnetto),
                        fsc: enforceNumber(fsc),
                        pefc: enforceNumber(pefc),
                        sortiment: row.typ === VirkeskopNettotyper.Snittnetto ? undefined : row.getSortiment(sortimentId)
                    };
                });

                this.utskriftMediatorService.updateUtskriftValues({
                    ...this.utskrift,
                    ...values,
                    kontraktId: this.kontrakt.id,
                    pristabell
                });
            });

        const utskriftValues$ = this.utskriftMediatorService.utskriftValues$.pipe(
            tap(utskrift => {
                if (!utskrift) {
                    return;
                }

                this.form?.patchValue({
                    ...utskrift
                }, {
                    emitEvent: false,
                    onlySelf: true
                });

                const rows = utskrift.pristabell;
                this.pristabellFormArray().clear({ emitEvent: false });
                rows.forEach(prisrad => this.addPrisrad(prisrad));
            })
        );

        combineLatest([
            utskriftValues$,
            this.kontraktMediatorService.kontrakt$
        ]
        ).pipe(
            takeUntilDestroyed()
        ).subscribe({
            next: ([
                utskrift,
                kontrakt
            ]) => {
                this.utskrift.set(utskrift);
                this.kontrakt = kontrakt;
            }
        });

        this.utskriftMediatorService.initUtskriftValues(this.canEdit());

        const leverantorer$ = this.leverantorMediatorService.leverantorer$.pipe(
            map(leverantorer => (_curr: LeverantorItem[]) => leverantorer)
        );

        const makeGodkannandeKravsEjLeverantorer$ = this.virkeskopskontraktLeverantorMediatorService.kontraktLeverantorer$.pipe(
            map(leverantorer => leverantorer
                .filter(l => l.makeGodkannandeKravsEj === true)
                .map(l => l.leverantorGuid)),
            tap(leverantorGuids => {
                this.selectedLeverantorer.patchValue(leverantorGuids, {
                    emitEvent: false,
                    onlySelf: true
                });
            }),
            map(() => (curr: LeverantorItem[]) => curr)
        );

        const leverantorerChange$ = this.selectedLeverantorer.valueChanges.pipe(
            withLatestFrom(this.kontraktMediatorService.kontrakt$),
            map(([
                leverantorGuids,
                kontrakt
            ]) => leverantorGuids.map(guid => ({
                leverantorGuid: guid,
                kontraktGuid: kontrakt.guid,
                virkeskopskontraktId: 0,
                virkeskopsavtalLeverantorId: 0
            }))),
            tap((leverantorer) => this.virkeskopskontraktLeverantorMediatorService.setMakeGodkannandeKravsEjSrc.next(leverantorer)),
            map(() => (curr: LeverantorItem[]) => curr)
        );

        this.leverantorItems$ = merge(leverantorer$, makeGodkannandeKravsEjLeverantorer$, leverantorerChange$).pipe(
            scan((acc: LeverantorItem[], mutatorFn: (curr: LeverantorItem[]) => []) => mutatorFn(acc), [])
        );

        merge(this.utskriftMediatorService.kontraktsbeloppChange$, this.form.valueChanges, this.kontraktMediatorService.kontraktUpdate$).pipe(
            withLatestFrom(this.kontraktMediatorService.kontrakt$),
            filter(([_, kontrakt]) => [
                VirkeskopAffarsformer.Leveransvirke,
                VirkeskopAffarsformer.AuFastKostnad,
                VirkeskopAffarsformer.AuSjalvkostnad
            ].includes(kontrakt.affarsform)),
            takeUntilDestroyed()
        ).subscribe(([_, kontrakt]) => {
            const isVisible = (!ObjectUtils.hasValue(kontrakt.skordarmatning) || !kontrakt.skordarmatning) &&
                this.utskrift()?.ersattningstyp === "Bruttovolym" &&
                this.utskrift()?.pristabell.some(rad => rad.hasPrislista);

            this.virkeskopValidationService.updateClientSideValidation(isVisible, {
                message: `Observera att prisrader med Prislista och Prislistehänvisning alltid beräknas med nettovolym`,
                messageId: "bruttoPrislista",
                severity: RuleSeverityLevel.warning,
                context: RuleErrorContext.save,
                entityGuids: [this.kontrakt?.guid],
                strategy: RuleVisibilityStrategy.alwaysShow
            });
        });
    }

    leverantorSearchFn = (val: string, item: LeverantorItem) => item.namn?.toUpperCase().includes(val?.toUpperCase()) ?? false;

    protected getIcon(): string {
        return this.validationService.getEntitySeverity("utskrift", this.kontrakt?.guid);
    }

    protected onAterstall() {
        const response$ = this.modalService.confirm(new ModalSettings("Återställning",
            "Vill du verkligen återställa? Alla förändringar du gjort kommer att ersättas med data från förkalkyl och åtgärder."));
        response$.pipe(
            take(1)
        ).subscribe({
            next: (res) => {
                if (res === ConfirmDialogResponse.save) {
                    this.aterstall();
                }
            }
        });
    }

    private aterstall() {
        this.utskriftMediatorService.resetUtskriftValues();
    }

    private pristabellFormArray() {
        return this.form?.controls.pristabell;
    }

    private addPrisrad(prisrad: PristabellRad) {
        const prisradFormGroup = new FormGroup<PrisradForm>({
            visasIUtskrift: new FormControl<boolean | null>(prisrad.visasIUtskrift),
            mattslag: new FormControl<string>(prisrad.mattslag),
            anteckning: new FormControl<string>(prisrad.anteckning),
            fsc: new FormControl<number | null>(prisrad.fsc),
            pefc: new FormControl<number | null>(prisrad.pefc),
            sortimentsnetto: new FormControl<number | null>(prisrad.sortimentsnetto),
            prisberakning: new FormControl<string>(prisrad.prisberakning),
            tillaggAvdrag: new FormControl<number | null>(prisrad.tillaggAvdrag),
            marknadsprislistaId: new FormControl<number | null>(prisrad.marknadsprislistaId),
            sortimentId: new FormControl<number | null>(prisrad.sortimentId),
            handelssortimentId: new FormControl<number | null>(prisrad.handelssortiment_Id),
            handelssortimentsgruppId: new FormControl<number | null>(prisrad.handelssortimentsgrupp_Id),
            kopieradRad: new FormControl<boolean>(prisrad.kopieradRad),
            inmatningshandelssortiment: new FormControl<string>(prisrad.inmatningshandelssortiment),
            uniqueKey: new FormControl<string>(prisrad.uniqueKey)
        });

        this.pristabellFormArray().push(prisradFormGroup, { emitEvent: false });
    }
}
